import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  User,
  Key,
  Save,
  Eye,
  EyeOff,
  Shield,
  FileText,
  Plug,
  Trash2,
  Edit,
  Plus,
  CreditCard,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { getCurrentUser, setCurrentUser } from "../utils/auth";
import ManageSubscription from "../components/ManageSubscription";
import { SUPPORTED_LANGUAGES } from "../i18n";
import "./Profile.css";

interface ProfileData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  preferredLanguage: string;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface TemplateForm {
  name: string;
  category: string;
  type: string;
  subject_template: string;
  body_template: string;
  is_ai_enabled: boolean;
  is_organization_template: boolean;
}

interface EmailTemplate {
  id: number;
  name: string;
  category: string;
  type: string;
  subject_template: string;
  body_template: string;
  is_ai_enabled: boolean;
  is_organization_template: boolean;
  created_by_name?: string;
}

interface Message {
  type: string;
  text: string;
}

interface CurrentUser {
  id: number;
  role: string;
}

interface UserProfile {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  preferredLanguage?: string;
}

interface ProfileResponse {
  success: boolean;
  user?: UserProfile;
  message?: string;
}

interface TemplatesResponse {
  success: boolean;
  data?: EmailTemplate[];
}

interface UpdateProfileResponse {
  success: boolean;
  error?: string;
  message?: string;
}

interface ChangePasswordResponse {
  success: boolean;
  error?: string;
  message?: string;
}

type TabType = "profile" | "templates" | "email" | "subscription";

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation("common");
  const queryClient = useQueryClient();
  const [profileData, setProfileData] = useState<ProfileData>({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    preferredLanguage: i18n.language || "en",
  });
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<Message>({ type: "", text: "" });
  const [activeTab, setActiveTab] = useState<TabType>("profile");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(
    null,
  );
  const [templateForm, setTemplateForm] = useState<TemplateForm>({
    name: "",
    category: "recruiter",
    type: "outreach",
    subject_template: "",
    body_template: "",
    is_ai_enabled: false,
    is_organization_template: false,
  });

  // Fetch user profile
  useQuery<ProfileResponse>(
    "user-profile",
    () => api.user.getProfile() as unknown as Promise<ProfileResponse>,
    {
      onSuccess: (data) => {
        if (data.success && data.user) {
          setProfileData({
            username: data.user.username || "",
            email: data.user.email || "",
            firstName: data.user.firstName || "",
            lastName: data.user.lastName || "",
            preferredLanguage:
              data.user.preferredLanguage || i18n.language || "en",
          });
        }
      },
    },
  );

  // Fetch email templates
  const { data: templatesResponse, isLoading: templatesLoading } =
    useQuery<TemplatesResponse>(
      "email-templates",
      () =>
        api.communication.getTemplates() as unknown as Promise<TemplatesResponse>,
      {
        enabled: activeTab === "templates",
      },
    );

  // Fetch user signatures
  useQuery("user-signatures", () => api.communication.getSignatures(), {
    enabled: activeTab === "templates",
  });

  const allTemplates = (templatesResponse?.data || []) as EmailTemplate[];

  // Separate organization templates from user templates
  const organizationTemplates = allTemplates.filter(
    (t) => t.is_organization_template,
  );
  const userTemplates = allTemplates.filter((t) => !t.is_organization_template);

  // Get user role for authorization
  const user = (getCurrentUser() || {}) as CurrentUser;
  const isAdminOrModerator =
    user.role === "application_admin" || user.role === "organization_admin";

  // Create template mutation
  const createTemplateMutation = useMutation(
    (templateData: TemplateForm) =>
      api.communication.createTemplate(templateData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("email-templates");
        setShowTemplateModal(false);
        setTemplateForm({
          name: "",
          category: "recruiter",
          type: "outreach",
          subject_template: "",
          body_template: "",
          is_ai_enabled: false,
          is_organization_template: false,
        });
        setEditingTemplate(null);
        setMessage({ type: "success", text: t("success.templateCreated") });
      },
      onError: (error: {
        response?: { data?: { error?: string; message?: string } };
      }) => {
        setMessage({
          type: "error",
          text:
            error.response?.data?.error ||
            error.response?.data?.message ||
            t("errors.failedToCreateTemplate"),
        });
      },
    },
  );

  // Update template mutation
  const updateTemplateMutation = useMutation(
    ({ id, data }: { id: number; data: TemplateForm }) =>
      api.communication.updateTemplate(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("email-templates");
        setShowTemplateModal(false);
        setEditingTemplate(null);
        setTemplateForm({
          name: "",
          category: "recruiter",
          type: "outreach",
          subject_template: "",
          body_template: "",
          is_ai_enabled: false,
          is_organization_template: false,
        });
        setMessage({ type: "success", text: t("success.templateUpdated") });
      },
      onError: (error: {
        response?: { data?: { error?: string; message?: string } };
      }) => {
        setMessage({
          type: "error",
          text:
            error.response?.data?.error ||
            error.response?.data?.message ||
            t("errors.failedToUpdateTemplate"),
        });
      },
    },
  );

  // Delete template mutation
  const deleteTemplateMutation = useMutation(
    (id: number) => api.communication.deleteTemplate(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("email-templates");
        setMessage({ type: "success", text: t("success.templateDeleted") });
      },
      onError: (error: {
        response?: { data?: { error?: string; message?: string } };
      }) => {
        setMessage({
          type: "error",
          text:
            error.response?.data?.error ||
            error.response?.data?.message ||
            t("errors.failedToDeleteTemplate"),
        });
      },
    },
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    setMessage({ type: "", text: "" });

    // Optimistically apply language change
    const previousLanguage = i18n.language;
    if (profileData.preferredLanguage !== previousLanguage) {
      i18n.changeLanguage(profileData.preferredLanguage);
      localStorage.setItem("i18nextLng", profileData.preferredLanguage);
    }

    // Update user cookie immediately
    const currentUser = getCurrentUser();
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        preferred_language: profileData.preferredLanguage,
      });
    }

    try {
      const response = (await api.user.updateProfile({
        email: profileData.email,
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        preferred_language: profileData.preferredLanguage,
      })) as unknown as UpdateProfileResponse;

      if (response.success) {
        setIsEditing(false);
        setMessage({ type: "success", text: t("success.profileUpdated") });
      } else {
        // Rollback language on failure
        i18n.changeLanguage(previousLanguage);
        localStorage.setItem("i18nextLng", previousLanguage);
        const freshUser = getCurrentUser();
        if (freshUser) {
          setCurrentUser({
            ...freshUser,
            preferred_language: previousLanguage,
          });
        }
        setMessage({
          type: "error",
          text:
            response.error ||
            response.message ||
            t("errors.failedToUpdateProfile"),
        });
      }

      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      // Rollback language on error
      i18n.changeLanguage(previousLanguage);
      localStorage.setItem("i18nextLng", previousLanguage);
      const freshUser = getCurrentUser();
      if (freshUser) {
        setCurrentUser({
          ...freshUser,
          preferred_language: previousLanguage,
        });
      }
      console.error("Error updating profile:", error);
      setMessage({
        type: "error",
        text: t("errors.failedToUpdateProfile"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: t("errors.passwordsDoNotMatch") });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: t("errors.passwordTooShort"),
      });
      return;
    }

    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = (await api.user.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      })) as unknown as ChangePasswordResponse;

      if (response.success) {
        setShowPasswordModal(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setMessage({ type: "success", text: t("success.passwordUpdated") });
      } else {
        setMessage({
          type: "error",
          text:
            response.error ||
            response.message ||
            t("errors.failedToUpdatePassword"),
        });
      }

      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error updating password:", error);
      setMessage({
        type: "error",
        text: t("errors.failedToUpdatePassword"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (editingTemplate) {
      updateTemplateMutation.mutate({
        id: editingTemplate.id,
        data: templateForm,
      });
    } else {
      createTemplateMutation.mutate(templateForm);
    }
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      category: template.category,
      type: template.type,
      subject_template: template.subject_template,
      body_template: template.body_template,
      is_ai_enabled: template.is_ai_enabled,
      is_organization_template: template.is_organization_template || false,
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = (id: number) => {
    if (window.confirm(t("confirmations.deleteTemplate"))) {
      deleteTemplateMutation.mutate(id);
    }
  };

  const handleConnectEmail = (provider: string) => {
    // This would typically open OAuth flow
    // For now, just show a message
    setMessage({
      type: "info",
      text: `${provider} connection will be implemented with OAuth flow`,
    });
    setTimeout(() => setMessage({ type: "", text: "" }), 3000);
  };

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: "", text: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="profile">
      <div className="profile-header">
        <h1>{t("profile.profileSettings")}</h1>
        <p>{t("profile.manageAccount")}</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      {/* Tabs */}
      <div className="profile-tabs">
        <button
          className={`tab-btn ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          <User size={16} /> {t("profile.personalInfo")}
        </button>
        <button
          className={`tab-btn ${activeTab === "templates" ? "active" : ""}`}
          onClick={() => setActiveTab("templates")}
        >
          <FileText size={16} /> {t("profile.emailTemplates")}
        </button>
        <button
          className={`tab-btn ${activeTab === "email" ? "active" : ""}`}
          onClick={() => setActiveTab("email")}
        >
          <Plug size={16} /> {t("profile.emailConnection")}
        </button>
        <button
          className={`tab-btn ${activeTab === "subscription" ? "active" : ""}`}
          onClick={() => setActiveTab("subscription")}
        >
          <CreditCard size={16} /> {t("profile.subscription")}
        </button>
      </div>

      {/* Tab Content */}
      <div className="profile-content">
        {activeTab === "profile" && (
          <div className="profile-tab-content">
            <div className="card">
              <div className="card-header">
                <h3>
                  <User size={18} /> {t("profile.personalInformation")}
                </h3>
                <button
                  className="btn-secondary"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  {isEditing ? t("actions.cancel") : t("actions.edit")}
                </button>
              </div>

              <form className="profile-form">
                <div className="form-group">
                  <label className="form-label" htmlFor="profile-username">
                    {t("form.username")}
                  </label>
                  <input
                    type="text"
                    id="profile-username"
                    name="username"
                    value={profileData.username}
                    className="form-input"
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="profile-email">
                    {t("form.email")}
                  </label>
                  <input
                    type="email"
                    id="profile-email"
                    name="email"
                    value={profileData.email}
                    onChange={handleChange}
                    className="form-input"
                    disabled={!isEditing}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="profile-firstName">
                    {t("form.firstName")}
                  </label>
                  <input
                    type="text"
                    id="profile-firstName"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleChange}
                    className="form-input"
                    disabled={!isEditing}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="profile-lastName">
                    {t("form.lastName")}
                  </label>
                  <input
                    type="text"
                    id="profile-lastName"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleChange}
                    className="form-input"
                    disabled={!isEditing}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="profile-language">
                    {t("profile.languagePreference")}
                  </label>
                  <select
                    id="profile-language"
                    value={profileData.preferredLanguage}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        preferredLanguage: e.target.value,
                      })
                    }
                    className="form-input"
                    disabled={!isEditing}
                  >
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.nativeName} ({lang.name})
                      </option>
                    ))}
                  </select>
                  <small className="form-hint">
                    {t("profile.languagePreferenceHint")}
                  </small>
                </div>

                {isEditing && (
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setIsEditing(false)}
                      disabled={isLoading}
                    >
                      {t("actions.cancel")}
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleSave}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        t("loading.saving")
                      ) : (
                        <>
                          <Save size={16} /> {t("actions.saveChanges")}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>

            <div className="card">
              <div className="card-header">
                <h3>
                  <Shield size={18} /> {t("profile.security")}
                </h3>
              </div>

              <div className="security-actions">
                <button
                  className="btn-secondary w-100 mb-2"
                  onClick={() => setShowPasswordModal(true)}
                >
                  <Key size={16} /> {t("profile.changePassword")}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "templates" && (
          <div className="templates-tab-content">
            <div className="card">
              <div className="card-header">
                <h3>
                  <FileText size={18} /> {t("profile.emailTemplates")}
                </h3>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setEditingTemplate(null);
                      setTemplateForm({
                        name: "",
                        category: "recruiter",
                        type: "outreach",
                        subject_template: "",
                        body_template: "",
                        is_ai_enabled: false,
                        is_organization_template: false,
                      });
                      setShowTemplateModal(true);
                    }}
                  >
                    <Plus size={16} /> {t("profile.newTemplate")}
                  </button>
                  {isAdminOrModerator && (
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setEditingTemplate(null);
                        setTemplateForm({
                          name: "",
                          category: "recruiter",
                          type: "outreach",
                          subject_template: "",
                          body_template: "",
                          is_ai_enabled: false,
                          is_organization_template: true,
                        });
                        setShowTemplateModal(true);
                      }}
                    >
                      <Plus size={16} /> {t("profile.newOrganizationTemplate")}
                    </button>
                  )}
                </div>
              </div>

              {templatesLoading && (
                <div className="loading-state">
                  {t("loading.loadingTemplates")}
                </div>
              )}
              {!templatesLoading &&
                organizationTemplates.length === 0 &&
                userTemplates.length === 0 && (
                  <div className="empty-state">
                    <p>{t("empty.noTemplatesYet")}</p>
                  </div>
                )}
              {!templatesLoading &&
                (organizationTemplates.length > 0 ||
                  userTemplates.length > 0) && (
                  <div className="templates-list">
                    {/* Organization Templates Section */}
                    {organizationTemplates.length > 0 && (
                      <div className="templates-section">
                        <h4 className="templates-section-title">
                          {t("profile.organizationTemplates")}
                          {isAdminOrModerator && (
                            <span className="section-badge">
                              {t("profile.youCanManageThese")}
                            </span>
                          )}
                        </h4>
                        {organizationTemplates.map((template) => (
                          <div
                            key={template.id}
                            className="template-item organization-template"
                          >
                            <div className="template-info">
                              <h4>{template.name}</h4>
                              <div className="template-meta">
                                <span className="badge badge-org">
                                  {t("profile.organization")}
                                </span>
                                <span className="badge badge-category">
                                  {template.category}
                                </span>
                                <span className="badge badge-type">
                                  {template.type}
                                </span>
                                {template.is_ai_enabled && (
                                  <span className="badge badge-ai">
                                    {t("profile.aiEnabled")}
                                  </span>
                                )}
                              </div>
                              <p className="template-preview">
                                {t("profile.subject")}:{" "}
                                {template.subject_template.substring(0, 60)}...
                              </p>
                              {template.created_by_name && (
                                <p className="template-author">
                                  {t("profile.createdBy")}:{" "}
                                  {template.created_by_name}
                                </p>
                              )}
                            </div>
                            {isAdminOrModerator && (
                              <div className="template-actions">
                                <button
                                  className="btn-sm btn-secondary"
                                  onClick={() => handleEditTemplate(template)}
                                >
                                  <Edit size={14} /> {t("actions.edit")}
                                </button>
                                <button
                                  className="btn-sm btn-danger"
                                  onClick={() =>
                                    handleDeleteTemplate(template.id)
                                  }
                                >
                                  <Trash2 size={14} /> {t("actions.delete")}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* User Templates Section */}
                    {userTemplates.length > 0 && (
                      <div className="templates-section">
                        <h4 className="templates-section-title">
                          {t("profile.myTemplates")}
                        </h4>
                        {userTemplates.map((template) => (
                          <div key={template.id} className="template-item">
                            <div className="template-info">
                              <h4>{template.name}</h4>
                              <div className="template-meta">
                                <span className="badge badge-category">
                                  {template.category}
                                </span>
                                <span className="badge badge-type">
                                  {template.type}
                                </span>
                                {template.is_ai_enabled && (
                                  <span className="badge badge-ai">
                                    {t("profile.aiEnabled")}
                                  </span>
                                )}
                              </div>
                              <p className="template-preview">
                                {t("profile.subject")}:{" "}
                                {template.subject_template.substring(0, 60)}...
                              </p>
                            </div>
                            <div className="template-actions">
                              <button
                                className="btn-sm btn-secondary"
                                onClick={() => handleEditTemplate(template)}
                              >
                                <Edit size={14} /> {t("actions.edit")}
                              </button>
                              <button
                                className="btn-sm btn-danger"
                                onClick={() =>
                                  handleDeleteTemplate(template.id)
                                }
                              >
                                <Trash2 size={14} /> {t("actions.delete")}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        )}

        {activeTab === "email" && (
          <div className="email-connection-tab-content">
            <div className="card">
              <div className="card-header">
                <h3>
                  <Plug size={18} /> {t("profile.emailAccountConnection")}
                </h3>
              </div>

              <div className="email-providers">
                <div className="provider-card">
                  <div className="provider-info">
                    <div className="provider-icon gmail">G</div>
                    <div>
                      <h4>{t("profile.gmail")}</h4>
                      <p>{t("profile.connectYourGmail")}</p>
                    </div>
                  </div>
                  <button
                    className="btn-primary"
                    onClick={() => handleConnectEmail("Gmail")}
                  >
                    {t("profile.connectGmail")}
                  </button>
                </div>

                <div className="provider-card">
                  <div className="provider-info">
                    <div className="provider-icon outlook">O</div>
                    <div>
                      <h4>{t("profile.outlook")}</h4>
                      <p>{t("profile.connectYourOutlook")}</p>
                    </div>
                  </div>
                  <button
                    className="btn-primary"
                    onClick={() => handleConnectEmail("Outlook")}
                  >
                    {t("profile.connectOutlook")}
                  </button>
                </div>
              </div>

              <div className="connection-info">
                <h4>{t("profile.howItWorks")}</h4>
                <ul>
                  <li>{t("profile.connectEmailToSend")}</li>
                  <li>{t("profile.credentialsSecure")}</li>
                  <li>{t("profile.canDisconnectAnytime")}</li>
                  <li>{t("profile.usingSystemEmail")}</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === "subscription" && (
          <div className="subscription-tab-content">
            <ManageSubscription />
          </div>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{t("profile.changePassword")}</h3>
              <button
                className="modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                x
              </button>
            </div>
            <form
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                handlePasswordUpdate();
              }}
            >
              <div className="form-group">
                <label className="form-label" htmlFor="current-password">
                  {t("form.currentPassword")}
                </label>
                <input
                  type="password"
                  id="current-password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="new-password">
                  {t("form.newPassword")}
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="new-password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="confirm-password">
                  {t("form.confirmNewPassword")}
                </label>
                <input
                  type="password"
                  id="confirm-password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowPasswordModal(false)}
                >
                  {t("actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t("loading.updating")
                    : t("profile.updatePassword")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h3>
                {editingTemplate
                  ? t("profile.editTemplate")
                  : t("profile.createTemplate")}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                }}
              >
                x
              </button>
            </div>
            <form onSubmit={handleTemplateSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="template-name">
                  {t("profile.templateName")}
                </label>
                <input
                  type="text"
                  id="template-name"
                  value={templateForm.name}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, name: e.target.value })
                  }
                  className="form-input"
                  required
                  placeholder="e.g., Initial Outreach"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="template-category">
                    {t("profile.category")}
                  </label>
                  <select
                    id="template-category"
                    value={templateForm.category}
                    onChange={(e) =>
                      setTemplateForm({
                        ...templateForm,
                        category: e.target.value,
                      })
                    }
                    className="form-input"
                    required
                  >
                    <option value="recruiter">{t("profile.recruiter")}</option>
                    <option value="sales">{t("profile.sales")}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="template-type">
                    {t("profile.type")}
                  </label>
                  <select
                    id="template-type"
                    value={templateForm.type}
                    onChange={(e) =>
                      setTemplateForm({ ...templateForm, type: e.target.value })
                    }
                    className="form-input"
                    required
                  >
                    <option value="outreach">{t("profile.outreach")}</option>
                    <option value="presentation">
                      {t("profile.presentation")}
                    </option>
                    <option value="follow-up">{t("profile.followUp")}</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={templateForm.is_ai_enabled}
                    onChange={(e) =>
                      setTemplateForm({
                        ...templateForm,
                        is_ai_enabled: e.target.checked,
                      })
                    }
                    style={{ marginRight: "8px" }}
                  />
                  {t("profile.enableAIGeneration")}
                </label>
              </div>

              {isAdminOrModerator && (
                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      checked={templateForm.is_organization_template}
                      onChange={(e) =>
                        setTemplateForm({
                          ...templateForm,
                          is_organization_template: e.target.checked,
                        })
                      }
                      style={{ marginRight: "8px" }}
                    />
                    {t("profile.organizationTemplate")}
                  </label>
                  <small className="form-hint">
                    {t("profile.organizationTemplatesNote")}
                  </small>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="template-subject">
                  {t("profile.subjectTemplate")}
                </label>
                <input
                  type="text"
                  id="template-subject"
                  value={templateForm.subject_template}
                  onChange={(e) =>
                    setTemplateForm({
                      ...templateForm,
                      subject_template: e.target.value,
                    })
                  }
                  className="form-input"
                  required
                  placeholder="e.g., Job Opportunity: {'{{job.title}}'}"
                />
                <small className="form-hint">
                  {t("profile.useDynamicContent")}
                </small>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="template-body">
                  {t("profile.bodyTemplate")}
                </label>
                <textarea
                  id="template-body"
                  value={templateForm.body_template}
                  onChange={(e) =>
                    setTemplateForm({
                      ...templateForm,
                      body_template: e.target.value,
                    })
                  }
                  className="form-input"
                  required
                  rows={10}
                  placeholder="Hello {{candidate.first_name}}, ..."
                />
                <small className="form-hint">
                  {t("profile.useDynamicContent")}
                </small>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                  }}
                >
                  {t("actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    createTemplateMutation.isLoading ||
                    updateTemplateMutation.isLoading
                  }
                >
                  {(() => {
                    if (
                      createTemplateMutation.isLoading ||
                      updateTemplateMutation.isLoading
                    ) {
                      return t("loading.saving");
                    }
                    return editingTemplate
                      ? t("profile.updateTemplate")
                      : t("profile.createTemplate");
                  })()}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
